import { PublicRoutes } from "@/constants/routes";
import { Bug, KeyRound, LockKeyhole, ShieldAlert } from "lucide-react";
import { DPO } from "./company";
import type { LegalDocument } from "./types";

export const SECURITY: LegalDocument = {
  key: "seguranca",
  path: PublicRoutes.SEGURANCA,
  eyebrow: "Segurança",
  title: "Segurança na Fidexa",
  seoTitle: "Segurança: como protegemos dados e mensagens",
  description:
    "Como a Fidexa protege os dados das lojas e dos clientes: onde ficam, quem acessa, cópias de segurança, incidentes, como reportar falhas e como evitar golpes.",
  intro:
    "Os dados que passam pela Fidexa são dinheiro e relacionamento de verdade. Aqui contamos o que fazemos para protegê-los, e também o que ainda não temos.",
  version: "1.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: LockKeyhole,
      title: "Conexão criptografada",
      text: "Todo o tráfego entre você, o site e a plataforma passa por HTTPS.",
    },
    {
      icon: KeyRound,
      title: "Acesso por perfil",
      text: "Cada usuário da loja vê só o que o perfil dele permite. Na Fidexa, só quem precisa acessa os dados.",
    },
    {
      icon: ShieldAlert,
      title: "Nunca pedimos senha",
      text: "Nem a Fidexa nem as lojas pedem senha, código de verificação ou dados de cartão pelo WhatsApp.",
    },
    {
      icon: Bug,
      title: "Achou uma falha? Conte para nós",
      text: "Quem reporta uma vulnerabilidade de boa-fé tem resposta em até 5 dias úteis.",
    },
  ],

  audiencePaths: [
    {
      audience: "cliente-da-loja",
      icon: ShieldAlert,
      label: "Recebi uma mensagem de cobrança",
      description: "Veja como confirmar se a mensagem é mesmo da loja.",
      sectionId: "golpes",
    },
    {
      audience: "lojista",
      icon: KeyRound,
      label: "Sou lojista",
      description: "Entenda como os dados da sua loja são protegidos.",
      sectionId: "onde-ficam-os-dados",
    },
    {
      audience: "visitante",
      icon: Bug,
      label: "Encontrei uma falha",
      description: "Saiba como reportar uma vulnerabilidade com segurança.",
      sectionId: "reportar-vulnerabilidade",
    },
  ],

  sections: [
    {
      id: "compromisso",
      title: "Nosso compromisso",
      summary:
        "Adotamos medidas técnicas e administrativas proporcionais aos dados que tratamos. Nenhum sistema é imune a falhas; por isso também temos um plano para quando algo der errado.",
      blocks: [
        {
          type: "paragraph",
          text: "A Fidexa adota medidas de segurança, técnicas e administrativas, aptas a proteger os dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou difusão, proporcionais à natureza dos dados e ao estado da técnica (LGPD, arts. 46 a 49).",
        },
      ],
    },
    {
      id: "onde-ficam-os-dados",
      title: "Onde os dados ficam",
      summary:
        "A plataforma e o banco de dados rodam em servidores da Hostinger. As cópias de segurança ficam na Microsoft Azure, um provedor separado.",
      blocks: [
        {
          type: "table",
          caption: "Infraestrutura da plataforma",
          columns: [
            { key: "o-que", header: "O quê", primary: true },
            { key: "onde", header: "Onde" },
          ],
          rows: [
            {
              "o-que": "Aplicação e banco de dados",
              onde: "Servidores da Hostinger.",
            },
            {
              "o-que": "Cópias de segurança (backups)",
              onde: "Armazenamento da Microsoft Azure, separado dos servidores principais.",
            },
            {
              "o-que": "Mensagens de WhatsApp",
              onde: "WhatsApp Business Platform, a API oficial da Meta.",
            },
            {
              "o-que": "Site fidexa.com.br",
              onde: "Vercel, com HTTPS.",
            },
          ],
        },
        {
          type: "paragraph",
          text: `A lista completa de fornecedores que tratam dados está na página [LGPD na Fidexa](${PublicRoutes.LGPD}#suboperadores).`,
        },
      ],
    },
    {
      id: "medidas",
      title: "Medidas que adotamos",
      summary:
        "Conexão criptografada, acesso por perfil, poucas pessoas com acesso interno, cópias de segurança fora dos servidores e envio pela API oficial do WhatsApp.",
      blocks: [
        {
          type: "list",
          items: [
            "**Criptografia em trânsito:** o site, a plataforma e as integrações usam conexão HTTPS (TLS).",
            "**Acesso por perfil:** cada usuário da loja tem login próprio e acesso só às lojas e funções que o perfil permite.",
            "**Acesso interno restrito:** na Fidexa, só a equipe que precisa para prestar suporte e manter a plataforma acessa os dados, com dever de sigilo.",
            "**Cópias de segurança:** guardadas em um provedor separado dos servidores, para recuperar a plataforma em caso de falha.",
            "**API oficial do WhatsApp:** as mensagens saem pela WhatsApp Business Platform da Meta, com os modelos de mensagem aprovados por ela.",
            "**Fornecedores avaliados:** só usamos fornecedores de infraestrutura com compromissos próprios de segurança e proteção de dados.",
          ],
        },
      ],
    },
    {
      id: "o-que-ainda-nao-temos",
      title: "O que ainda não temos",
      summary:
        "A Fidexa não tem certificações como ISO 27001 ou SOC 2. Preferimos dizer isso a deixar você supor o contrário.",
      blocks: [
        {
          type: "paragraph",
          text: "A Fidexa não tem hoje certificação ISO 27001, relatório SOC 2 ou teste de invasão feito por empresa independente. Nossos fornecedores de infraestrutura têm certificações próprias, mas elas se referem à infraestrutura deles, não à Fidexa. Quando isso mudar, esta página será atualizada.",
        },
      ],
    },
    {
      id: "o-que-cabe-a-loja",
      title: "O que cabe à loja",
      summary:
        "Senha é pessoal. A loja cuida das credenciais dos usuários dela e dá acesso só a quem precisa.",
      blocks: [
        {
          type: "list",
          items: [
            "Não compartilhar login e senha entre funcionários: cada pessoa com o seu acesso.",
            "Remover o acesso de quem sai da loja no mesmo dia.",
            "Usar senhas fortes e diferentes das usadas em outros serviços.",
            "Avisar a Fidexa logo que suspeitar de acesso indevido à conta.",
          ],
        },
      ],
    },
    {
      id: "incidentes",
      title: "Se acontecer um incidente",
      summary:
        "Investigamos, contemos o problema e avisamos a loja afetada em até 48 horas após a confirmação.",
      blocks: [
        {
          type: "paragraph",
          text: `Ao identificar um incidente de segurança, agimos para contê-lo, investigamos a causa e registramos as medidas tomadas. Se houver risco ou dano relevante a dados de uma loja, avisamos a loja em até 48 horas após a confirmação, para que ela, como controladora, comunique a ANPD e os titulares no prazo legal. O passo a passo está em [LGPD na Fidexa](${PublicRoutes.LGPD}#incidentes).`,
        },
      ],
    },
    {
      id: "reportar-vulnerabilidade",
      title: "Como reportar uma vulnerabilidade",
      summary: `Encontrou uma falha de segurança? Escreva para ${DPO.email}. Respondemos em até 5 dias úteis e não tomamos medida contra quem reporta de boa-fé.`,
      blocks: [
        {
          type: "paragraph",
          text: `Envie para [${DPO.email}](mailto:${DPO.email}) a descrição da falha, os passos para reproduzi-la e o impacto que você imagina. Respondemos em até 5 dias úteis e mantemos você informado até a correção.`,
        },
        {
          type: "subheading",
          text: "Ao testar, por favor, não:",
        },
        {
          type: "list",
          items: [
            "Acesse, copie, altere ou apague dados de lojas ou de clientes além do mínimo necessário para demonstrar a falha.",
            "Faça testes que derrubem ou degradem o serviço, como ataques de negação de serviço.",
            "Use engenharia social, phishing ou acesso físico contra a equipe, as lojas ou os clientes.",
            "Divulgue a falha antes de ela ser corrigida.",
          ],
        },
        {
          type: "paragraph",
          text: "Quem seguir estas regras e agir de boa-fé não sofrerá nenhuma medida da Fidexa por isso. Não temos programa de recompensa em dinheiro.",
        },
      ],
    },
    {
      id: "golpes",
      title: "Golpes: como saber se a mensagem é da loja",
      summary:
        "A loja fala com você pelo WhatsApp sobre o seu crediário, mas nunca pede senha, código ou dados de cartão. Antes de pagar, confira quem recebe o Pix.",
      blocks: [
        {
          type: "paragraph",
          text: "Golpistas usam o nome de lojas e de empresas para cobrar dívidas falsas. Quando uma loja usa a Fidexa, as mensagens saem pela API oficial do WhatsApp, em nome da loja em que você comprou.",
        },
        {
          type: "list",
          items: [
            "**Nunca informe senha, código de verificação recebido por SMS ou dados de cartão.** Nem a loja nem a Fidexa pedem isso.",
            "**Confira o recebedor do Pix** antes de confirmar o pagamento: o nome tem de ser o da loja em que você comprou.",
            "**Desconfie de pressa:** ameaças, prazos de minutos e descontos bons demais são sinais de golpe.",
            "**Na dúvida, fale com a loja** pelo telefone ou pelo endereço que você já conhece, não pelo contato da mensagem suspeita.",
            "**Não reconhece a compra?** Avise a loja. Se a mensagem citar a Fidexa, escreva para o nosso encarregado.",
          ],
        },
        {
          type: "callout",
          tone: "warning",
          title: "Caiu em um golpe?",
          text: "Avise o seu banco na hora para tentar bloquear o valor pelo Mecanismo Especial de Devolução do Pix e registre um boletim de ocorrência.",
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-12",
      changes: ["Primeira versão publicada."],
    },
  ],
};
